import { Components, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';

const SIZES = {
  BUTTON: 40,
  ACTIVE_THUMB: 28,
  INACTIVE_THUMB: 16,
  CHECKED_THUMB: 24,
  ROOT_HEIGHT: 32,
  ROOT_WIDTH: 52,
  SMALL_ROOT_HEIGHT: 24,
  SMALL_ROOT_WIDTH: 40,
  SMALL_BUTTON: 36,
  SMALL_ACTIVE_THUMB: 20,
  SMALL_INACTIVE_THUMB: 12,
  SMALL_CHECKED_THUMB: 16,
};

const TRANSITION_TIME = '300ms';

export const MuiSwitch = {
  defaultProps: {
    disableRipple: true,
  },
  styleOverrides: {
    sizeSmall: ({ theme }) => ({
      width: SIZES.SMALL_ROOT_WIDTH,
      height: SIZES.SMALL_ROOT_HEIGHT,
      '& .MuiSwitch-thumb': {
        width: SIZES.SMALL_INACTIVE_THUMB,
        height: SIZES.SMALL_INACTIVE_THUMB,
      },
      '& .MuiSwitch-track': {
        borderRadius: SIZES.SMALL_ROOT_HEIGHT / 2,
        height: SIZES.SMALL_ROOT_HEIGHT,
      },
      '& .MuiSwitch-switchBase': {
        top: theme.spacing(-0.75),
        width: SIZES.SMALL_BUTTON,
        height: SIZES.SMALL_BUTTON,
        '&:active': {
          '& .MuiSwitch-thumb': {
            width: SIZES.SMALL_ACTIVE_THUMB,
            height: SIZES.SMALL_ACTIVE_THUMB,
          },
        },
        '&.Mui-checked': {
          left: theme.spacing(-0.75),
          '&:active': {
            '& .MuiSwitch-thumb': {
              width: SIZES.SMALL_ACTIVE_THUMB,
              height: SIZES.SMALL_ACTIVE_THUMB,
            },
          },
          '& .MuiSwitch-thumb': {
            width: SIZES.SMALL_CHECKED_THUMB,
            height: SIZES.SMALL_CHECKED_THUMB,
          },
        },
      },
    }),
    sizeMedium: ({ theme }) => ({
      width: SIZES.ROOT_WIDTH,
      height: SIZES.ROOT_HEIGHT,
      '& .MuiSwitch-thumb': {
        width: SIZES.INACTIVE_THUMB,
        height: SIZES.INACTIVE_THUMB,
      },
      '& .MuiSwitch-track': {
        borderRadius: SIZES.ROOT_HEIGHT / 2,
        height: SIZES.ROOT_HEIGHT,
      },
      '& .MuiSwitch-switchBase': {
        top: theme.spacing(-0.5),
        width: SIZES.BUTTON,
        height: SIZES.BUTTON,
        '&:active': {
          '& .MuiSwitch-thumb': {
            width: SIZES.ACTIVE_THUMB,
            height: SIZES.ACTIVE_THUMB,
          },
        },
        '&.Mui-checked': {
          left: 'unset',
          '&:active': {
            '& .MuiSwitch-thumb': {
              width: SIZES.ACTIVE_THUMB,
              height: SIZES.ACTIVE_THUMB,
            },
          },
          '& .MuiSwitch-thumb': {
            width: SIZES.CHECKED_THUMB,
            height: SIZES.CHECKED_THUMB,
          },
        },
      }
    }),
    root: ({ theme, ownerState }) => {
      return {
        boxSizing: 'border-box',
        padding: 0,
        overflow: 'visible',
        '& .MuiSwitch-switchBase': {
          padding: 0,
          boxSizing: 'border-box',
          left: theme.spacing(-0.75),
          transitionDuration: TRANSITION_TIME,
          '&:hover': {
            backgroundColor: alpha(theme.palette.neutral.onSurface, theme.palette.action.hoverOpacity),
          },
          '&.Mui-focusVisible': {
            backgroundColor: alpha(theme.palette.neutral.onSurface, theme.palette.action.focusOpacity),
          },
          '&:active': {
            backgroundColor: alpha(theme.palette.neutral.onSurface, theme.palette.action.activatedOpacity),
          },
          '&.Mui-checked': {
            transform: 'translateX(16px)',
            margin: 0,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
            },
            '&.Mui-focusVisible': {
              backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.focusOpacity),
            },
            '&:active': {
              backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
            },
            '& + .MuiSwitch-track': {
              backgroundColor: theme.palette[ownerState.color!].main,
              opacity: 1,
              border: 0,
            },
            '&.Mui-disabled': {
              '& .MuiSwitch-thumb': {
                backgroundColor: theme.palette.neutral.surface,
              },
              '& + .MuiSwitch-track': {
                backgroundColor: theme.palette.action.disabledBackground,
              }
            },
            '& .MuiSwitch-thumb': {
              backgroundColor: theme.palette.primary.container,
            },
          },
          '&.Mui-disabled': {
            '& .MuiSwitch-thumb': {
              backgroundColor: theme.palette.action.disabled,
            },
            '& .MuiSwitch-track': {
              borderColor: theme.palette.action.disabledBorder,
            }
          },
        },
        '& .MuiSwitch-thumb': {
          boxSizing: 'border-box',
          backgroundColor: theme.palette.neutral.outline,
          transition: `width ${TRANSITION_TIME}, height ${TRANSITION_TIME}`,
        },
        '& .MuiSwitch-track': {
          boxSizing: 'border-box',
          backgroundColor: 'transparent',
          opacity: 1,
          border: '2px solid',
          borderColor: theme.palette.neutral.onSurfaceVariant,
        },
      };
    },
    input: {
      left: 'unset',
      width: 'unset',
    }
  },
} as Components<Theme>['MuiSwitch'];
